import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FileEarmarkPdf, Save } from 'react-bootstrap-icons';
import { SelectableShift, TipData, SalesSession, extractShiftsFromSessions, TipDistributionConfig, TipRecord, transformTipRecordFromAPI, DistributionMode, IndividualSavePayload } from './types';
import { transformShiftsToTipData } from './utils/transformShifts';
import { ShiftSelectModal, TipRecordsList, TipDistributionTable, TipDistributionPDFModal, DistributionModeModal, IndividualTipView } from './components';
import { getHistoricalSalesStatisticsByLocalId } from '../../../../crud/management_api/sales_statistics';
import { getAllTipRecordsByLocalId, createTipRecord } from '../../../../crud/payments_api/tip_management';
import './TipManagement.scss';

type ViewMode = 'list' | 'distribution' | 'individual';

const TipManagement: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [isShiftSelectOpen, setIsShiftSelectOpen] = useState(false);
  const [selectedShifts, setSelectedShifts] = useState<SelectableShift[]>([]);
  const [currentTipData, setCurrentTipData] = useState<TipData | null>(null);
  const [salesSessions, setSalesSessions] = useState<SalesSession[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [distributionConfig, setDistributionConfig] = useState<TipDistributionConfig | null>(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [tipRecords, setTipRecords] = useState<TipRecord[]>([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModeSelectOpen, setIsModeSelectOpen] = useState(false);
  const [distributionMode, setDistributionMode] = useState<DistributionMode>('percentage');
  const [isSavingIndividual, setIsSavingIndividual] = useState(false);
  const [savedIndividualData, setSavedIndividualData] = useState<IndividualSavePayload | null>(null);

  // Handle config changes from the distribution table
  const handleConfigChange = useCallback((config: TipDistributionConfig) => {
    setDistributionConfig(config);
  }, []);

  // Handle PDF export - open modal
  const handleExportPDF = useCallback(() => {
    if (!distributionConfig) return;
    setShowPDFModal(true);
  }, [distributionConfig]);

  // Get local_id from user context
  const getLocalId = (): number => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const parsed = JSON.parse(user);
        return parsed.local_id || 1;
      } catch (e) {
        console.error('Error parsing user context:', e);
        return 1;
      }
    }
    return 1; // default local_id if not found
  };

  // Fetch sales sessions on mount
  useEffect(() => {
    const fetchSessions = async () => {
      setIsLoadingSessions(true);
      try {
        const localId = getLocalId();
        const response = await getHistoricalSalesStatisticsByLocalId(localId);
        if (response.data) {
          setSalesSessions(response.data);
        }
      } catch (error) {
        console.error('Error fetching sales sessions:', error);
        setSalesSessions([]);
      } finally {
        setIsLoadingSessions(false);
      }
    };

    fetchSessions();
  }, []);

  // Fetch tip records
  const fetchTipRecords = useCallback(async () => {
    setIsLoadingRecords(true);
    try {
      const localId = getLocalId();
      const response: any = await getAllTipRecordsByLocalId(localId);
      if (response.data && response.data.records) {
        const transformed = response.data.records.map(transformTipRecordFromAPI);
        setTipRecords(transformed);
      }
    } catch (error) {
      console.error('Error fetching tip records:', error);
      setTipRecords([]);
    } finally {
      setIsLoadingRecords(false);
    }
  }, []);

  useEffect(() => {
    fetchTipRecords();
  }, [fetchTipRecords]);

  // Extract all available shifts from sales sessions
  const availableShifts = useMemo(() => {
    return extractShiftsFromSessions(salesSessions);
  }, [salesSessions]);

  const handleCreateNew = () => {
    setIsModeSelectOpen(true);
  };

  const handleModeSelectClose = () => {
    setIsModeSelectOpen(false);
  };

  const handleModeSelectConfirm = (mode: DistributionMode) => {
    setDistributionMode(mode);
    setIsModeSelectOpen(false);
    setIsShiftSelectOpen(true);
  };

  const handleShiftSelectClose = () => {
    setIsShiftSelectOpen(false);
  };

  const handleShiftSelectConfirm = (shifts: SelectableShift[]) => {
    setSelectedShifts(shifts);
    if (distributionMode === 'individual') {
      setViewMode('individual');
    } else {
      const tipData = transformShiftsToTipData(shifts);
      setCurrentTipData(tipData);
      setViewMode('distribution');
    }
    setIsShiftSelectOpen(false);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedShifts([]);
    setCurrentTipData(null);
    setDistributionMode('percentage');
    setSavedIndividualData(null);
  };

  const handleRecordSaved = () => {
    // Refresh the list and go back
    fetchTipRecords();
    handleBackToList();
  };

  const handleSaveRecord = useCallback(async () => {
    if (!distributionConfig || isSaving) return;

    try {
      setIsSaving(true);

      // Get local_id from localStorage
      const userContext = localStorage.getItem('user');
      let localId = 1;
      if (userContext) {
        try {
          const parsed = JSON.parse(userContext);
          localId = parsed.local_id || 1;
        } catch (e) {
          console.error('Error parsing user context:', e);
        }
      }

      // Import the function at the top if needed
      const { createTipRecord } = await import('../../../../crud/payments_api/tip_management');
      const { toast } = await import('react-toastify');

      // Build the payload
      const daysPayload: any = {};
      const dates = Object.keys(currentTipData?.days || {});
      
      dates.forEach(date => {
        const usersForDay: any[] = [];
        
        distributionConfig.groups.forEach(group => {
          if (group.isRealGroup && group.users.length > 0) {
            group.users.forEach(user => {
              usersForDay.push({
                user_id: parseInt(user.id.replace('user-', '')) || 0,
                user_name: user.name,
                rate: user.rate,
                group: group.name,
                attended: user.attendance,
              });
            });
          } else if (!group.isRealGroup) {
            usersForDay.push({
              user_id: parseInt(group.id.replace('user-', '')) || 0,
              user_name: group.name,
              rate: group.rate,
              attended: group.attendance,
            });
          }
        });

        daysPayload[date] = {
          value: currentTipData?.days[date]?.value || 0,
          users: usersForDay,
        };
      });

      const payload = {
        data: {
          start_date: distributionConfig.startDate,
          end_date: distributionConfig.endDate,
          total_tips: distributionConfig.totals.declared,
          total_distributed: distributionConfig.totals.distributed,
          total_employees: distributionConfig.groups.reduce((sum, g) => {
            if (g.isRealGroup) return sum + g.users.length;
            return sum + 1;
          }, 0),
          days: daysPayload,
        },
      };

      const response = await createTipRecord(localId, payload.data);
      
      if (response) {
        toast.success('Registro guardado exitosamente');
        handleRecordSaved();
      } else {
        toast.error('Error al guardar el registro');
      }
    } catch (error) {
      console.error('Error saving tip record:', error);
      const { toast } = await import('react-toastify');
      toast.error('Error al guardar el registro');
    } finally {
      setIsSaving(false);
    }
  }, [distributionConfig, currentTipData, isSaving, handleRecordSaved]);

  const handleSaveIndividual = useCallback(async (payload: IndividualSavePayload) => {
    if (isSavingIndividual) return;

    try {
      setIsSavingIndividual(true);
      const localId = getLocalId();
      const { toast } = await import('react-toastify');

      const response = await createTipRecord(localId, payload);

      if (response) {
        toast.success('Registro individual guardado exitosamente');
        handleRecordSaved();
      } else {
        toast.error('Error al guardar el registro');
      }
    } catch (error) {
      console.error('Error saving individual tip record:', error);
      const { toast } = await import('react-toastify');
      toast.error('Error al guardar el registro');
    } finally {
      setIsSavingIndividual(false);
    }
  }, [isSavingIndividual, handleRecordSaved]);

  const handleViewRecord = (record: TipRecord) => {
    if (!record.rawData) {
      console.error('No raw data available for this record');
      return;
    }

    if (record.recordType === 'individual') {
      // Individual record — store the saved payload and go to individual view
      setSavedIndividualData(record.rawData as IndividualSavePayload);
      setSelectedShifts([]); // No shifts — rendering from saved data
      setViewMode('individual');
    } else {
      // Percentage record — reconstruct TipData from saved record
      const tipData: TipData = {
        start_date: record.start_date,
        end_date: record.end_date,
        days: record.rawData.days,
        total_tips: record.total_tips,
        total_distributed: record.total_distributed,
        total_employees: record.employee_count,
      };

      setCurrentTipData(tipData);
      setSelectedShifts([]); // No shifts selected when viewing a saved record
      setViewMode('distribution');
    }
  };

  return (
    <div className="tip-management">
      {isLoadingSessions || isLoadingRecords ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      ) : viewMode === 'list' ? (
        <TipRecordsList
          records={tipRecords}
          onCreateNew={handleCreateNew}
          onViewRecord={handleViewRecord}
          onRecordUpdated={fetchTipRecords}
        />
      ) : viewMode === 'individual' ? (
        <IndividualTipView
          selectedShifts={selectedShifts}
          onBack={handleBackToList}
          onSave={savedIndividualData ? undefined : handleSaveIndividual}
          isSaving={isSavingIndividual}
          isViewMode={!!savedIndividualData}
          savedData={savedIndividualData || undefined}
          initialMode={savedIndividualData?.mode}
        />
      ) : (
        <div className="distribution-view">
          <div className="distribution-header">
            <button className="btn btn-back" onClick={handleBackToList}>
              ← Volver
            </button>
            <div className="distribution-info">
              <h2>
                {selectedShifts.length > 0 
                  ? 'Nueva Distribución de Propinas' 
                  : 'Detalle de Distribución de Propinas'}
              </h2>
              <p>
                {currentTipData?.start_date} - {currentTipData?.end_date}
                {selectedShifts.length > 0 && (
                  <> • {selectedShifts.length} turno{selectedShifts.length !== 1 ? 's' : ''}</>
                )}
              </p>
            </div>
            {selectedShifts.length > 0 && (
              <button 
                className="btn btn-save" 
                onClick={handleSaveRecord}
                disabled={!distributionConfig || isSaving}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-sm"></span>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Crear Registro
                  </>
                )}
              </button>
            )}
            <button 
              className="btn btn-export-pdf" 
              onClick={handleExportPDF}
              disabled={!distributionConfig}
            >
              <FileEarmarkPdf size={18} />
              Exportar PDF
            </button>
          </div>

          {currentTipData && (
            <TipDistributionTable
              tipData={currentTipData}
              selectedShifts={selectedShifts}
              onConfigChange={handleConfigChange}
              onRecordSaved={handleRecordSaved}
              isViewMode={selectedShifts.length === 0}
              onSaveRecord={selectedShifts.length > 0 ? handleSaveRecord : undefined}
              isSaving={isSaving}
            />
          )}
        </div>
      )}

      <ShiftSelectModal
        isOpen={isShiftSelectOpen}
        onClose={handleShiftSelectClose}
        onConfirm={handleShiftSelectConfirm}
        shifts={availableShifts}
        maxSelections={7}
      />

      <DistributionModeModal
        isOpen={isModeSelectOpen}
        onClose={handleModeSelectClose}
        onConfirm={handleModeSelectConfirm}
      />

      <TipDistributionPDFModal
        show={showPDFModal}
        onHide={() => setShowPDFModal(false)}
        config={distributionConfig}
      />
    </div>
  );
};

export default TipManagement;